import { Menu, X } from 'lucide-react';
import { Link } from "react-router-dom";
import { useState } from "react";
import ClickSpark from './ClickSpark';

// Simple Button component since we're not using shadcn
const Button = ({ variant = "default", size = "default", children, className = "", ...props }) => {
  const baseClasses = "inline-flex items-center justify-center font-medium transition-all duration-200";
  
  const sizeClasses = {
    default: "px-4 py-2",
    sm: "px-3 py-1 text-sm",
    icon: "p-2"
  };
  
  const variantClasses = variant === "outline" 
    ? "border-2 border-black bg-white text-black hover:bg-gray-50 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]"
    : "bg-[#0395fc] text-white border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]";
  
  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses} ${className}`;

  return (
    <button className={combinedClasses} {...props}>
      {children}
    </button>
  );
};

const Navbar = ({
  logo = {
    url: "/",
    src: "/DMLSLogo.png",
    alt: "DMLS Logo",
    title: "DMLS",
  },
  menu = [
    { title: "Home", url: "/" },
    {
      title: "Events",
      url: "/events",
    },
    {
      title: "Careers",
      url: "/careers",
    },
    {
      title: "Team",
      url: "/team",
    },
    {
      title: "Article of the Week",
      url: "/article-of-the-week",
    },
  ],
  auth = {
    login: { title: "Members", url: "/login" },
    signup: { title: "Join Us", url: "https://airtable.com/appbzvXorTGCwSsy9/pagrrVOHqpocEY5IX/form" },
  },
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <ClickSpark sparkColor="#000" sparkSize={10} sparkRadius={18} sparkCount={8} duration={400}>
      <section className="py-4 bg-white border-b-2 border-black sticky top-0 z-50 lg:relative lg:z-auto">
        <div className="container mx-auto px-4">
          {/* Desktop Menu */}
          <nav className="hidden justify-between lg:flex">
            <div className="flex items-center gap-6">
              {/* Logo */}
              <Link to={logo.url} className="flex items-center gap-2">
                <img
                  src={logo.src}
                  className="max-h-8"
                  alt={logo.alt}
                />
                <span className="text-xl font-black font-[var(--font-anton)] tracking-tight">
                  {logo.title}
                </span>
              </Link>
              <div className="flex items-center">
                <div className="flex items-center gap-6">
                  {menu.map((item) => renderMenuItem(item))}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="border-2 border-black font-black">
                <a href={auth.signup.url} target="_blank" rel="noopener noreferrer">{auth.signup.title}</a>
              </Button>
            </div>
          </nav>

          {/* Mobile Menu */}
          <div className="block lg:hidden">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link to={logo.url} className="flex items-center gap-2" onClick={closeMobileMenu}>
                <img
                  src={logo.src}
                  className="max-h-8"
                  alt={logo.alt}
                />
                <span className="text-lg font-black font-[var(--font-anton)] tracking-tight">
                  {logo.title}
                </span>
              </Link>
              <Button 
                variant="outline" 
                size="icon" 
                className="border-2 border-black"
                onClick={toggleMobileMenu}
              >
                {isMobileMenuOpen ? <X className="size-4" /> : <Menu className="size-4" />}
              </Button>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
              <div className="absolute top-full left-0 right-0 bg-white border-b-2 border-black shadow-lg z-50">
                <div className="flex flex-col py-4">
                  {/* Navigation Links */}
                  <div className="flex flex-col space-y-2 px-4">
                    {menu.map((item) => (
                      <Link
                        key={item.title}
                        to={item.url}
                        className="px-4 py-3 text-sm font-black hover:text-primary hover:bg-gray-50 transition-colors border-l-4 border-transparent hover:border-primary"
                        onClick={closeMobileMenu}
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>

                  {/* Auth Buttons */}
                  <div className="flex flex-col gap-2 px-4 mt-4 pt-4 border-t border-gray-200">
                    <Button size="sm" className="border-2 border-black font-black w-full justify-center">
                      <a href={auth.signup.url} target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu}>{auth.signup.title}</a>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </ClickSpark>
  );
};

const renderMenuItem = (item) => {
  if (item.items) {
    return (
      <div key={item.title} className="relative group">
        <button className="flex items-center gap-1 px-4 py-2 text-sm font-black hover:text-primary transition-colors">
          {item.title}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className="absolute top-full left-0 mt-2 w-80 bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
          {item.items.map((subItem) => (
            <SubMenuLink key={subItem.title} item={subItem} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <Link
      key={item.title}
      to={item.url}
      className="px-4 py-2 text-sm font-black hover:text-primary transition-colors"
    >
      {item.title}
    </Link>
  );
};

const SubMenuLink = ({ item }) => {
  return (
    <a
      className="flex gap-4 p-4 hover:bg-gray-50 transition-colors border-b border-gray-200 last:border-b-0"
      href={item.url}
    >
      <div className="text-primary">{item.icon}</div>
      <div>
        <div className="text-sm font-black text-foreground">{item.title}</div>
        {item.description && (
          <p className="text-sm text-muted-foreground leading-snug mt-1">
            {item.description}
          </p>
        )}
      </div>
    </a>
  );
};

export default Navbar;